/* oxlint-disable typescript/no-explicit-any -- schema library requires flexible any types */
/* oxlint-disable typescript/no-unsafe-assignment, typescript/no-unsafe-call, typescript/no-unsafe-member-access -- schema library uses any types throughout */
import { BaseSchema } from '../core/base-schema'
import type * as errors from '../core/errors'
import * as types from '../core/types'
import type * as util from '../core/util'
import type { DefaultSchema } from './wrappers'
import { _default as makeDefault } from './wrappers'
import type { OptionalSchema, NullableSchema } from '../core/base-schema'

export interface BigintSchemaDef extends types.SchemaTypeDef {
  type: 'bigint' | 'int64' | 'uint64' | 'sint64' | 'fixed64' | 'sfixed64'
}

export interface BigintSchemaInternals extends types.SchemaInternals<
  bigint,
  bigint
> {
  def: BigintSchemaDef
  bag: util.LoosePartial<{
    minimum: bigint
    maximum: bigint
  }>
}

export interface BigintSchema extends types.Schema<
  bigint,
  bigint,
  BigintSchemaInternals
> {
  positive(message?: string | { message: string | (() => string) }): this
  negative(message?: string | { message: string | (() => string) }): this
  gt(
    value: bigint,
    message?: string | { message: string | (() => string) },
  ): this
  gte(
    value: bigint,
    message?: string | { message: string | (() => string) },
  ): this
  min(
    value: bigint,
    message?: string | { message: string | (() => string) },
  ): this
  lt(
    value: bigint,
    message?: string | { message: string | (() => string) },
  ): this
  lte(
    value: bigint,
    message?: string | { message: string | (() => string) },
  ): this
  max(
    value: bigint,
    message?: string | { message: string | (() => string) },
  ): this
  optional(): OptionalSchema<this>
  nullable(): NullableSchema<this>
  default(defaultValue: bigint | (() => bigint)): DefaultSchema<this>
  meta(metadata: Record<string, any>): this
  parse(data: unknown, params?: types.ParseContext): bigint
  safeParse(
    data: unknown,
    params?: types.ParseContext,
  ):
    | { success: true; data: bigint }
    | { success: false; error: errors.SchemaError }
}

export const BigintSchema = types.$constructor<BigintSchema>(
  'BigintSchema',
  (inst, def: BigintSchemaDef) => {
    BaseSchema.init(inst, def)

    inst._zod.parse = (payload, _ctx) => {
      if (typeof payload.value === 'bigint') return payload

      payload.issues.push({
        code: 'invalid_type',
        expected: 'bigint',
        input: payload.value,
        inst,
      })
      return payload
    }

    const createCheck = (
      checkFn: types.CheckFn<bigint>,
      onattach: (schema: any) => void = () => {},
    ) => ({
      _zod: {
        def: { check: 'custom' },
        check: checkFn,
        onattach: [onattach],
      },
    })

    const addCheck = (check: types.Check<bigint>) => {
      return new BigintSchema({
        ...def,
        checks: [...(def.checks ?? []), check],
      })
    }

    inst.positive = (message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          if (payload.value > 0n) return
          const msg = getMessage()
          payload.issues.push({
            code: 'too_small',
            continue: true,
            type: 'bigint',
            minimum: 0n,
            inclusive: false,
            input: payload.value,
            inst,
            ...(msg
              ? { message: typeof msg === 'function' ? msg() : msg }
              : {}),
          })
        }),
      )
    }

    inst.negative = (message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          if (payload.value < 0n) return
          const msg = getMessage()
          payload.issues.push({
            code: 'too_big',
            continue: true,
            type: 'bigint',
            maximum: 0n,
            inclusive: false,
            input: payload.value,
            inst,
            ...(msg
              ? { message: typeof msg === 'function' ? msg() : msg }
              : {}),
          })
        }),
      )
    }

    inst.gt = (value, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          if (payload.value > value) return
          const msg = getMessage()
          payload.issues.push({
            code: 'too_small',
            continue: true,
            type: 'bigint',
            minimum: value,
            inclusive: false,
            input: payload.value,
            inst,
            ...(msg
              ? { message: typeof msg === 'function' ? msg() : msg }
              : {}),
          })
        }),
      )
    }

    inst.gte = (value, message) => {
      return inst.min(value, message)
    }

    inst.min = (value, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck(
          (payload) => {
            if (payload.value >= value) return
            const msg = getMessage()
            payload.issues.push({
              code: 'too_small',
            continue: true,
              type: 'bigint',
              minimum: value,
              inclusive: true,
              input: payload.value,
              inst,
              ...(msg
                ? { message: typeof msg === 'function' ? msg() : msg }
                : {}),
            })
          },
          (schema) => {
            const curr = schema._zod.bag.minimum
            if (curr === undefined || value > curr)
              schema._zod.bag.minimum = value
          },
        ),
      )
    }

    inst.lt = (value, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck((payload) => {
          if (payload.value < value) return
          const msg = getMessage()
          payload.issues.push({
            code: 'too_big',
            continue: true,
            type: 'bigint',
            maximum: value,
            inclusive: false,
            input: payload.value,
            inst,
            ...(msg
              ? { message: typeof msg === 'function' ? msg() : msg }
              : {}),
          })
        }),
      )
    }

    inst.lte = (value, message) => {
      return inst.max(value, message)
    }

    inst.max = (value, message) => {
      const getMessage = () =>
        typeof message === 'string' ? message : message?.message
      return addCheck(
        createCheck(
          (payload) => {
            if (payload.value <= value) return
            const msg = getMessage()
            payload.issues.push({
              code: 'too_big',
            continue: true,
              type: 'bigint',
              maximum: value,
              inclusive: true,
              input: payload.value,
              inst,
              ...(msg
                ? { message: typeof msg === 'function' ? msg() : msg }
                : {}),
            })
          },
          (schema) => {
            const curr = schema._zod.bag.maximum
            if (curr === undefined || value < curr)
              schema._zod.bag.maximum = value
          },
        ),
      )
    }

    inst.default = (defaultValue: bigint | (() => bigint)) =>
      makeDefault(inst, defaultValue)
  },
)

export function bigint(
  message?: string | { message: string | (() => string) },
): BigintSchema {
  return new BigintSchema({
    type: 'bigint',
    ...(message
      ? { error: typeof message === 'string' ? message : message.message }
      : {}),
  })
}

export function int64(): BigintSchema {
  return bigintScalar('int64')
    .min(-(2n ** 63n))
    .max(2n ** 63n - 1n)
}

export function uint64(): BigintSchema {
  return bigintScalar('uint64')
    .min(0n)
    .max(2n ** 64n - 1n)
}

export function sint64(): BigintSchema {
  return bigintScalar('sint64')
    .min(-(2n ** 63n))
    .max(2n ** 63n - 1n)
}

export function fixed64(): BigintSchema {
  return bigintScalar('fixed64')
    .min(0n)
    .max(2n ** 64n - 1n)
}

export function sfixed64(): BigintSchema {
  return bigintScalar('sfixed64')
    .min(-(2n ** 63n))
    .max(2n ** 63n - 1n)
}

function bigintScalar(
  type: 'int64' | 'uint64' | 'sint64' | 'fixed64' | 'sfixed64',
): BigintSchema {
  const wireType =
    type === 'fixed64' || type === 'sfixed64' ? 'fixed64' : 'varint'
  return new BigintSchema({
    type,
    proto: {
      scalar: { type, wireType },
    },
  })
}
